// Visitor Service - Direkter Supabase Client im Frontend
import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

// Initialize Supabase Client (mit anon key für Frontend)
function getSupabaseClient() {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Missing Supabase environment variables');
    throw new Error('Supabase configuration missing');
  }

  return createClient(supabaseUrl, supabaseAnonKey);
}

// Validate and sanitize source parameter
function validateSource(source) {
  const validSources = ['organic', 'newsletter', 'meta-ads', 'social-media', 'direct'];
  const sanitized = source?.toLowerCase().trim().replace(/[^a-z0-9-]/g, '');
  return validSources.includes(sanitized) ? sanitized : 'other';
}

/**
 * Register a visitor and get their visitor number
 * @param {string} fragebogenType - 'A' or 'B'
 * @param {string|null} panelId - Optional Panel-ID für Bilendi/respondi Integration
 * @param {string|null} existingSessionId - Optional vorhandene Session-ID
 */
export async function registerVisitor(fragebogenType, panelId = null, existingSessionId = null) {
  try {
    // Get source from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const rawSource = urlParams.get('source') || (panelId ? 'panel' : 'direct');
    const source = validateSource(rawSource);

    // Use provided session ID or get from localStorage or generate new
    let sessionId = existingSessionId || localStorage.getItem('survey_session_id');

    if (!sessionId) {
      sessionId = uuidv4();
      localStorage.setItem('survey_session_id', sessionId);
      console.log('Neue Session-ID generiert:', sessionId.substring(0, 8) + '...');
    } else {
      console.log('Session-ID verwendet:', sessionId.substring(0, 8) + '...');
    }

    const supabase = getSupabaseClient();

    // Check if visitor already exists
    const { data: existingVisitor, error: checkError } = await supabase
      .from('panel_visitors')
      .select('id, created_at')
      .eq('session_id', sessionId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      // PGRST116 = not found, which is expected for new visitors
      throw checkError;
    }

    if (existingVisitor) {
      // Visitor already registered - get their visitor number
      const { count, error: countError } = await supabase
        .from('panel_visitors')
        .select('*', { count: 'exact', head: true })
        .lte('created_at', existingVisitor.created_at);

      if (countError) throw countError;

      return {
        success: true,
        visitorNumber: count,
        sessionId: sessionId,
        isNew: false
      };
    }

    // Register new visitor
    const { data: newVisitor, error: insertError } = await supabase
      .from('panel_visitors')
      .insert([
        {
          session_id: sessionId,
          fragebogen_type: fragebogenType,
          completed: false,
          screened_out: false,
          panel_id: panelId || null, // NEU: Panel-ID speichern
          source: source
        }
      ])
      .select()
      .single();

    if (insertError) throw insertError;

    // Get total visitor count
    const { count, error: countError } = await supabase
      .from('panel_visitors')
      .select('*', { count: 'exact', head: true });

    if (countError) throw countError;

    console.log('New visitor registered:', {
      visitorNumber: count,
      sessionId: sessionId.substring(0, 8) + '...',
      fragebogenType,
      source,
      panelId: panelId ? panelId.substring(0, 8) + '...' : 'none'
    });

    return {
      success: true,
      visitorNumber: count,
      sessionId: sessionId,
      isNew: true,
      panelId: panelId
    };

  } catch (error) {
    console.error('Error registering visitor:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Mark visitor as completed
 */
export async function markVisitorCompleted(sessionId) {
  try {
    const supabase = getSupabaseClient();

    const { error } = await supabase
      .from('panel_visitors')
      .update({ completed: true })
      .eq('session_id', sessionId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error marking visitor as completed:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Check if quota is reached
 * @param {number} maxQuota - Maximum number of participants (default: 1100)
 * @returns {Promise<{quotaReached: boolean, currentCount: number, maxQuota: number}>}
 */
export async function checkQuota(maxQuota = 1200) {
  try {
    const supabase = getSupabaseClient();
    
    // Zähle nur Panel-Visitors, die completed = true haben
    // Wichtig: Quota basiert auf abgeschlossenen Fragebögen, nicht auf Starts
    const { count, error } = await supabase
      .from('panel_visitors')
      .select('*', { count: 'exact', head: true })
      .not('panel_id', 'is', null)
      .eq('completed', true);
    
    if (error) throw error;
    
    console.log('Quota Check (completed only):', {
      current: count,
      max: maxQuota,
      reached: count >= maxQuota,
      remaining: Math.max(0, maxQuota - count)
    });
    
    return {
      quotaReached: count >= maxQuota,
      currentCount: count,
      maxQuota: maxQuota,
      remaining: Math.max(0, maxQuota - count)
    };
    
  } catch (error) {
    console.error('Error checking quota:', error);
    // Bei Fehler: Nicht blockieren (graceful degradation)
    return {
      quotaReached: false,
      currentCount: 0,
      maxQuota: maxQuota,
      error: error.message
    };
  }
}

/**
 * Mark visitor as screened out
 */
export async function markVisitorScreenedOut(sessionId, reason = 'other') {
  try {
    console.log('markVisitorScreenedOut aufgerufen:', {
      sessionId: sessionId.substring(0, 8) + '...',
      reason
    });
    
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('panel_visitors')
      .update({ 
        screened_out: true,
        screenout_reason: reason
      })
      .eq('session_id', sessionId)
      .select(); // NEU: Select um zu sehen was aktualisiert wurde

    if (error) {
      console.error('Supabase Update Error:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      console.warn('⚠️ Kein Visitor gefunden mit session_id:', sessionId.substring(0, 8) + '...');
      return { success: false, error: 'Visitor not found' };
    }
    
    console.log('✅ Visitor aktualisiert:', data);

    return { success: true, data };
  } catch (error) {
    console.error('Error marking visitor as screened out:', error);
    return {
      success: false,
      error: error.message
    };
  }
}
